// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import type {
  WellbeingService,
  WellbeingServiceContent,
} from "@modules/service/reducer/wellbeing-services/types";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetWellbeingServicesList,
  GetWellbeingServiceDetails,
  AddWellbeingService,
  EditWellbeingService,
  EditWellbeingServiceAssignments,
} from "@modules/service/constants/actions";
import type { PaginationType } from "@types";
import config from "@config";

type WellbeingServicesRequest = $Exact<{
  clientId: string,
}>;

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type WellbeingServicesResponse = $Exact<{
  content: Array<WellbeingService>,
  pagination: PaginationType,
  totalElements: number,
}>;

export function* onGetWellbeingServicesList({
  payload,
}: Action<WellbeingServicesRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<WellbeingServicesResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service`,
      {
        params: {
          limit: config.results100PerPage,
        },
      },
    );

    yield put(actionCreators.GetWellbeingServicesListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetWellbeingServicesListError(e.response));
  }
}

type WellbeingServiceRequest = WellbeingServicesRequest &
  $Exact<{
    providerId: string,
  }>;

export function* onGetWellbeingServiceDetails({
  payload,
}: Action<WellbeingServiceRequest>): Generator<*, void, { data: ItemResponse<WellbeingService> }> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service/${payload.providerId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service/${payload.providerId}/assignment`,
    );

    yield all([
      put(actionCreators.GetWellbeingServiceDetailsSuccess(stableResponse.data)),
      put(actionCreators.SetWellbeingServiceAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetWellbeingServiceDetailsError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  providerId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetWellbeingServicesList({ clientId: params.clientId }));
    if (params.providerId) {
      yield put(
        actionCreators.GetWellbeingServiceDetails({
          clientId: params.clientId,
          providerId: params.providerId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

// CREATE

type WellbeingServiceAddRequest = WellbeingServicesRequest &
  $Exact<{
    draft: WellbeingService,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddWellbeingService({
  payload,
}: Action<WellbeingServiceAddRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<WellbeingService>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.SetWellbeingServiceDraft(null)),
      put(actionCreators.GetWellbeingServiceDetailsSuccess(null)),
      put(actionCreators.AddWellbeingServiceSuccess(response.data)),
      put(actionCreators.SetShowAddWellbeingServiceModal({ show: false })),
      call(onRefresh, { clientId: payload.clientId }),
    ]);
  } catch (e) {
    yield put(actionCreators.AddWellbeingServiceError(e.response));
  }
}

// UPDATE

type WellbeingServiceUpdateRequest = WellbeingServiceRequest &
  $Exact<{
    draft: WellbeingServiceContent,
  }>;

export function* onEditWellbeingService({
  payload,
}: Action<WellbeingServiceUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<WellbeingService>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/wellbeing/service/${payload.providerId}`,
      payload.draft,
    );

    yield all([
      put(actionCreators.SetWellbeingServiceDraft(null)),
      put(actionCreators.GetWellbeingServiceDetailsSuccess(null)),
      put(actionCreators.EditWellbeingServiceSuccess(response.data)),
      put(actionCreators.SetShowEditWellbeingServiceModal({ show: false })),
      call(onRefresh, { clientId: payload.clientId }),
    ]);
  } catch (e) {
    yield put(actionCreators.EditWellbeingServiceError(e.response));
  }
}

type WellbeingServiceUpdateAssignmentsRequest = $Exact<{
  container: WellbeingService,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onEditWellbeingServiceAssignments({
  payload,
}: Action<WellbeingServiceUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/wellbeing/service/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/wellbeing/service/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/wellbeing/service/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.EditWellbeingServiceAssignmentsSuccess()),
      put(actionCreators.SetWellbeingServiceAssignments([])),
      put(actionCreators.ShowEditAssignmentsModal(false)),
      call(onRefresh, { clientId: payload.container.clientId }),
    ]);
  } catch (e) {
    yield put(actionCreators.EditWellbeingServiceAssignmentsError(e.message));
  }
}

// ROOT

export function* WellbeingServiceSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetWellbeingServicesList, onGetWellbeingServicesList),
    takeEvery(GetWellbeingServiceDetails, onGetWellbeingServiceDetails),
    takeEvery(AddWellbeingService, onAddWellbeingService),
    takeEvery(EditWellbeingService, onEditWellbeingService),
    takeEvery(EditWellbeingServiceAssignments, onEditWellbeingServiceAssignments),
  ]);
}

export default WellbeingServiceSagas;
